import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import {
  fmtMoney, fmtDate, fmtDateTime, statusPill, fulfilmentPill, computeDueDate, gstSplit,
  statusAfterPayment, INVOICE_STATUSES, TERMS, FULFILMENT, CURRENCIES, PAYMENT_METHODS,
} from './b2bLib';
import { fetchB2BXeroMatch, xeroChip } from './b2bXeroMatch';
import { isSkutopiaWired } from './b2bSkutopia';
import '../po/drawer.css';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function InvoiceDrawer({ invoiceId, session, fx, onClose, onChanged }) {
  const toast = useToast();
  const [inv, setInv] = useState(null);
  const [lines, setLines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tracking, setTracking] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // inline form state
  const [pay, setPay] = useState({ amount: '', paid_on: todayISO(), method: 'Bank Transfer', remittance_ref: '' });
  const [note, setNote] = useState({ type: 'note', body: '' });
  const [trk, setTrk] = useState({ carrier: '', tracking_url: '' });
  const [newLine, setNewLine] = useState({ sku: '', description: '', qty: '', unit_price: '' });
  const [ed, setEd] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [i, l, p, a, t] = await Promise.all([
      aws.from('b2b_invoice_computed').select('*').eq('id', invoiceId),
      aws.from('b2b_invoice_lines').select('*').eq('invoice_id', invoiceId),
      aws.from('b2b_payments').select('*').eq('invoice_id', invoiceId).order('paid_on', { ascending: false }),
      aws.from('b2b_activity').select('*').eq('invoice_id', invoiceId).order('occurred_at', { ascending: false }),
      aws.from('b2b_tracking').select('*').eq('invoice_id', invoiceId),
    ]);
    const row = (i.data || [])[0] || null;
    setInv(row);
    setLines(l.data || []);
    setPayments(p.data || []);
    setActivity(a.data || []);
    setTracking(t.data || []);
    setEd(row ? {
      invoice_ref: row.invoice_ref || '', po_ref: row.po_ref || '', sales_order_ref: row.sales_order_ref || '',
      skutopia_url: row.skutopia_url || '', issue_date: row.issue_date || '', terms: row.terms || '',
      currency: row.currency || 'AUD', amount_inc_gst: row.amount_inc_gst ?? '', fulfilment_status: row.fulfilment_status || 'Not Sent',
      eta: row.eta || '',
    } : null);
    setLoading(false);
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  // Catalogue for the line-item SKU picker (load once; independent of invoice).
  useEffect(() => {
    aws.from('forecaster_catalog').select('id,sku_name,sku_code').eq('is_active', true).neq('status', 'retired').order('sku_name')
      .then(({ data }) => setCatalog(data || []));
  }, []);

  async function run(fn, okMsg) {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged && onChanged(); }
    catch (e) { const { title, body } = humanizeError(e, 'Write failed'); toast.error(title, body); }
    finally { setBusy(false); }
  }

  const logActivity = (type, body) =>
    authWrite(session, 'b2b_activity', 'insert', { invoice_id: invoiceId, occurred_at: new Date().toISOString(), actor: session?.name || session?.code || 'staff', type, body });

  // ── actions ────────────────────────────────────────────────────────────────
  const setStatus = (status, actType, msg) => run(async () => {
    const patch = { status, updated_at: new Date().toISOString() };
    if (status === 'Issued' && !inv.due_date && inv.issue_date && inv.terms) patch.due_date = computeDueDate(inv.issue_date, inv.terms);
    await authWrite(session, 'b2b_invoices', 'update', patch, { column: 'id', value: invoiceId });
    await logActivity(actType, msg);
  }, msg);

  const addPayment = () => {
    const amt = Number(pay.amount);
    if (!amt || amt <= 0) { toast.error('Enter an amount', 'Payment must be greater than zero.'); return; }
    run(async () => {
      await authWrite(session, 'b2b_payments', 'insert', {
        invoice_id: invoiceId, paid_on: pay.paid_on || todayISO(), amount: amt,
        method: pay.method || null, remittance_ref: pay.remittance_ref || null, created_by: session?.code || null,
      });
      const newPaid = Number(inv.paid_to_date || 0) + amt;
      const next = statusAfterPayment(newPaid, inv.amount_inc_gst, inv.status);
      if (next !== inv.status) await authWrite(session, 'b2b_invoices', 'update', { status: next, updated_at: new Date().toISOString() }, { column: 'id', value: invoiceId });
      await logActivity('payment', `Payment ${fmtMoney(amt, inv.currency)} (${pay.method || 'n/a'})${next !== inv.status ? ` → ${next}` : ''}`);
      setPay({ amount: '', paid_on: todayISO(), method: 'Bank Transfer', remittance_ref: '' });
    }, 'Payment logged');
  };

  const markPaid = () => {
    const bal = Number(inv.balance || 0);
    run(async () => {
      if (bal > 0.005) await authWrite(session, 'b2b_payments', 'insert', { invoice_id: invoiceId, paid_on: todayISO(), amount: bal, method: 'Bank Transfer', notes: 'Marked paid (settling balance)', created_by: session?.code || null });
      await authWrite(session, 'b2b_invoices', 'update', { status: 'Paid', updated_at: new Date().toISOString() }, { column: 'id', value: invoiceId });
      await logActivity('payment', `Marked paid${bal > 0.005 ? ` (settled ${fmtMoney(bal, inv.currency)})` : ''}`);
    }, 'Marked paid');
  };

  const addNote = () => {
    if (!note.body.trim()) return;
    run(async () => { await logActivity(note.type, note.body.trim()); setNote({ type: 'note', body: '' }); }, 'Added');
  };

  const addTracking = () => {
    if (!trk.tracking_url.trim()) return;
    run(async () => {
      await authWrite(session, 'b2b_tracking', 'insert', { invoice_id: invoiceId, carrier: trk.carrier || null, tracking_url: trk.tracking_url.trim() });
      setTrk({ carrier: '', tracking_url: '' });
    }, 'Tracking added');
  };
  const delTracking = (id) => run(() => authWrite(session, 'b2b_tracking', 'delete', null, { column: 'id', value: id }), 'Removed');
  const delPayment = (p) => run(async () => {
    await authWrite(session, 'b2b_payments', 'delete', null, { column: 'id', value: p.id });
    const newPaid = Number(inv.paid_to_date || 0) - Number(p.amount || 0);
    const next = statusAfterPayment(newPaid, inv.amount_inc_gst, inv.status === 'Paid' ? 'Issued' : inv.status);
    if (next !== inv.status) await authWrite(session, 'b2b_invoices', 'update', { status: next }, { column: 'id', value: invoiceId });
    await logActivity('note', `Payment of ${fmtMoney(p.amount, inv.currency)} reversed`);
  }, 'Payment removed');

  // ── line items (SKU × units — feeds wholesale demand) ───────────────────────
  const pickSku = (code) => {
    const c = catalog.find(x => x.sku_code === code);
    setNewLine(s => ({ ...s, sku: code, description: s.description || (c?.sku_name || '') }));
  };
  const addLine = () => {
    const qty = Number(newLine.qty);
    const price = newLine.unit_price === '' ? null : Number(newLine.unit_price);
    if (!qty || qty <= 0) { toast.error('Enter units', 'Quantity must be greater than zero.'); return; }
    if (!newLine.sku && !newLine.description.trim()) { toast.error('Pick a SKU', 'Choose a SKU or type a description.'); return; }
    run(async () => {
      const line_total = price == null ? null : Math.round(qty * price * 100) / 100;
      await authWrite(session, 'b2b_invoice_lines', 'insert', {
        invoice_id: invoiceId, sku: newLine.sku || null, description: newLine.description.trim() || null,
        qty, unit_price: price, line_total,
      });
      await logActivity('note', `Line added: ${newLine.sku || newLine.description.trim()} × ${qty}`);
      setNewLine({ sku: '', description: '', qty: '', unit_price: '' });
    }, 'Line added');
  };
  const delLine = (id) => run(() => authWrite(session, 'b2b_invoice_lines', 'delete', null, { column: 'id', value: id }), 'Line removed');

  const saveEdit = () => {
    const incRaw = ed.amount_inc_gst === '' ? null : Number(ed.amount_inc_gst);
    const { ex, gst } = incRaw == null ? { ex: null, gst: null } : gstSplit(incRaw, ed.currency);
    const patch = {
      invoice_ref: ed.invoice_ref || null, po_ref: ed.po_ref || null, sales_order_ref: ed.sales_order_ref || null,
      skutopia_url: ed.skutopia_url || null, issue_date: ed.issue_date || null, terms: ed.terms || null,
      currency: ed.currency, amount_inc_gst: incRaw, amount_ex_gst: ex, gst,
      fulfilment_status: ed.fulfilment_status,
      due_date: ed.issue_date && ed.terms ? computeDueDate(ed.issue_date, ed.terms) : (inv.due_date || null),
      eta: ed.eta || null, updated_at: new Date().toISOString(),
    };
    run(async () => { await authWrite(session, 'b2b_invoices', 'update', patch, { column: 'id', value: invoiceId }); setEditing(false); }, 'Saved');
  };

  const xero = xeroChip(inv ? fetchB2BXeroMatch(inv) : null);
  const lineUnits = lines.reduce((s, l) => s + Number(l.qty || 0), 0);
  const lineValue = lines.reduce((s, l) => s + Number(l.line_total || 0), 0);

  return createPortal(
    <>
      <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(3px)' }} />
      <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 201, width: 620, maxWidth: '96vw', background: '#fff', boxShadow: '-12px 0 48px rgba(0,0,0,0.16)', display: 'flex', flexDirection: 'column' }}>
        {loading || !inv ? (
          <div style={{ padding: 40, color: '#a1a1aa' }}>Loading…</div>
        ) : (
          <>
            {/* header */}
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e4e4e7', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#0a0a0a' }}>{inv.customer_name}</div>
                  <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>
                    {inv.invoice_ref || '—'}{inv.po_ref ? ` · PO ${inv.po_ref}` : ''}
                  </div>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#a1a1aa' }}>×</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#0F1419', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(inv.balance, inv.currency)}</span>
                <span style={{ fontSize: 11, color: '#a1a1aa' }}>balance of {inv.amount_inc_gst == null ? '—' : fmtMoney(inv.amount_inc_gst, inv.currency)}</span>
                <Pill p={statusPill(inv.status, inv.is_overdue)} />
                {inv.is_overdue && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>{inv.days_overdue}d overdue</span>}
              </div>
            </div>

            {/* action bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 22px', borderBottom: '1px solid #f4f4f5', flexShrink: 0 }}>
              <ActBtn disabled={busy} onClick={() => setStatus(inv.status, 'approval', 'Approved')}>Approve</ActBtn>
              <ActBtn disabled={busy || inv.status !== 'Draft'} onClick={() => setStatus('Issued', 'sent', 'Marked issued')}>Mark Issued</ActBtn>
              <ActBtn disabled={busy} onClick={() => setStatus(inv.status, 'reminder', 'Reminder logged')}>Send Reminder</ActBtn>
              <ActBtn disabled={busy || inv.status === 'Paid'} primary onClick={markPaid}>Mark Paid</ActBtn>
              <ActBtn disabled={busy || inv.status === 'Cancelled'} danger onClick={() => setStatus('Cancelled', 'hold', 'Cancelled')}>Cancel</ActBtn>
              <div style={{ flex: 1 }} />
              <ActBtn disabled={busy} onClick={() => setEditing(v => !v)}>{editing ? 'Close edit' : 'Edit details'}</ActBtn>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
              {/* fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                <Field label="Amount inc GST" value={inv.amount_inc_gst == null ? '—' : fmtMoney(inv.amount_inc_gst, inv.currency)} />
                <Field label="Ex GST" value={inv.amount_ex_gst == null ? '—' : fmtMoney(inv.amount_ex_gst, inv.currency)} />
                <Field label="GST" value={inv.gst == null ? '—' : fmtMoney(inv.gst, inv.currency)} />
                <Field label="Paid to date" value={fmtMoney(inv.paid_to_date, inv.currency)} accent="#16a34a" />
                <Field label="Balance" value={fmtMoney(inv.balance, inv.currency)} />
                <Field label="Currency" value={inv.currency} />
                <Field label="Terms" value={inv.terms || '—'} />
                <Field label="Issued" value={fmtDate(inv.issue_date)} />
                <Field label="Due" value={fmtDate(inv.due_date)} accent={inv.is_overdue ? '#dc2626' : undefined} />
                <Field label="Fulfilment" value={inv.fulfilment_status} />
                <Field label="ETA" value={fmtDate(inv.eta)} />
                <Field label="Ageing" value={inv.ageing_bucket} />
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                {inv.sales_order_ref && <span style={{ fontSize: 11, color: '#71717a' }}>SO {inv.sales_order_ref}</span>}
                {inv.skutopia_url && <a href={inv.skutopia_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#7c3aed' }}>Skutopia ↗</a>}
                <span title={isSkutopiaWired() ? 'Synced from Skutopia' : 'Skutopia live sync not wired yet — SO ref / fulfilment / tracking are imported or manual'}
                  style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: isSkutopiaWired() ? '#dcfce7' : '#f4f4f5', color: isSkutopiaWired() ? '#16a34a' : '#a1a1aa' }}>
                  {isSkutopiaWired() ? 'Skutopia synced' : 'Skutopia: manual'}
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: xero.bg, color: xero.fg }}>{xero.label}</span>
              </div>

              {editing && (
                <div style={{ background: '#fafafa', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <FG label="Invoice ref"><input className="form-input" value={ed.invoice_ref} onChange={e => setEd(s => ({ ...s, invoice_ref: e.target.value }))} /></FG>
                    <FG label="PO ref"><input className="form-input" value={ed.po_ref} onChange={e => setEd(s => ({ ...s, po_ref: e.target.value }))} /></FG>
                    <FG label="Sales order ref"><input className="form-input" value={ed.sales_order_ref} onChange={e => setEd(s => ({ ...s, sales_order_ref: e.target.value }))} /></FG>
                    <FG label="Skutopia URL"><input className="form-input" value={ed.skutopia_url} onChange={e => setEd(s => ({ ...s, skutopia_url: e.target.value }))} /></FG>
                    <FG label="Issue date"><input type="date" className="form-input" value={ed.issue_date} onChange={e => setEd(s => ({ ...s, issue_date: e.target.value }))} /></FG>
                    <FG label="Terms"><select className="form-select" value={ed.terms} onChange={e => setEd(s => ({ ...s, terms: e.target.value }))}><option value="">—</option>{TERMS.map(t => <option key={t}>{t}</option>)}</select></FG>
                    <FG label="Currency"><select className="form-select" value={ed.currency} onChange={e => setEd(s => ({ ...s, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></FG>
                    <FG label="Amount inc GST"><input type="number" step="0.01" className="form-input" value={ed.amount_inc_gst} onChange={e => setEd(s => ({ ...s, amount_inc_gst: e.target.value }))} /></FG>
                    <FG label="Fulfilment"><select className="form-select" value={ed.fulfilment_status} onChange={e => setEd(s => ({ ...s, fulfilment_status: e.target.value }))}>{FULFILMENT.map(f => <option key={f}>{f}</option>)}</select></FG>
                    <FG label="ETA"><input type="date" className="form-input" value={ed.eta} onChange={e => setEd(s => ({ ...s, eta: e.target.value }))} /></FG>
                  </div>
                  <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 8 }}>Due auto-recomputes from issue date + terms{ed.issue_date && ed.terms ? `: ${fmtDate(computeDueDate(ed.issue_date, ed.terms))}` : ''}.</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                    <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={saveEdit}>Save details</button>
                  </div>
                </div>
              )}

              {/* line items — SKU × units (feeds wholesale demand) */}
              <Section title={`Line items (${lines.length})`}>
                {lines.map(l => (
                  <Row key={l.id}>
                    <span>{l.sku ? <b>{l.sku}</b> : ''} {l.description}</span>
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number(l.qty)} u{l.unit_price != null ? ` × ${fmtMoney(l.unit_price, inv.currency)} = ${fmtMoney(l.line_total, inv.currency)}` : ''}
                      </span>
                      <button onClick={() => delLine(l.id)} disabled={busy} style={delBtn}>×</button>
                    </span>
                  </Row>
                ))}
                {lines.length === 0 && <Empty>No line items yet — add SKUs + units below.</Empty>}
                {lines.length > 0 && (
                  <Row>
                    <span style={{ fontWeight: 600, color: '#0a0a0a' }}>{lineUnits} units</span>
                    <span style={{ fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(lineValue, inv.currency)}</span>
                  </Row>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select value={newLine.sku} onChange={e => pickSku(e.target.value)} style={miniInput(150)}>
                    <option value="">SKU…</option>
                    {catalog.map(c => <option key={c.id} value={c.sku_code}>{c.sku_code} — {c.sku_name}</option>)}
                  </select>
                  <input placeholder="Description" value={newLine.description} onChange={e => setNewLine(s => ({ ...s, description: e.target.value }))} style={{ ...miniInput(0), flex: 1, minWidth: 90 }} />
                  <input type="number" step="0.001" placeholder="Units" value={newLine.qty} onChange={e => setNewLine(s => ({ ...s, qty: e.target.value }))} style={miniInput(70)} />
                  <input type="number" step="0.01" placeholder="Unit $" value={newLine.unit_price} onChange={e => setNewLine(s => ({ ...s, unit_price: e.target.value }))} style={miniInput(80)} />
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={addLine}>Add line</button>
                </div>
              </Section>

              {/* payments */}
              <Section title={`Payments (${payments.length})`}>
                {payments.map(p => (
                  <Row key={p.id}>
                    <span>{fmtDate(p.paid_on)} · {p.method || '—'}{p.remittance_ref ? ` · ${p.remittance_ref}` : ''}{p.notes ? ` · ${p.notes}` : ''}</span>
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <b style={{ color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(p.amount, inv.currency)}</b>
                      <button onClick={() => delPayment(p)} disabled={busy} style={delBtn}>×</button>
                    </span>
                  </Row>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="number" step="0.01" placeholder="Amount" value={pay.amount} onChange={e => setPay(s => ({ ...s, amount: e.target.value }))} style={miniInput(90)} />
                  <input type="date" value={pay.paid_on} onChange={e => setPay(s => ({ ...s, paid_on: e.target.value }))} style={miniInput(130)} />
                  <select value={pay.method} onChange={e => setPay(s => ({ ...s, method: e.target.value }))} style={miniInput(120)}>{PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}</select>
                  <input placeholder="Remittance ref" value={pay.remittance_ref} onChange={e => setPay(s => ({ ...s, remittance_ref: e.target.value }))} style={miniInput(120)} />
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={addPayment}>Log payment</button>
                </div>
              </Section>

              {/* tracking */}
              <Section title={`Tracking (${tracking.length})`}>
                {tracking.map(t => (
                  <Row key={t.id}>
                    <a href={t.tracking_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.carrier || 'Carrier'} ↗</a>
                    <button onClick={() => delTracking(t.id)} disabled={busy} style={delBtn}>×</button>
                  </Row>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                  <input placeholder="Carrier" value={trk.carrier} onChange={e => setTrk(s => ({ ...s, carrier: e.target.value }))} style={miniInput(110)} />
                  <input placeholder="Tracking URL" value={trk.tracking_url} onChange={e => setTrk(s => ({ ...s, tracking_url: e.target.value }))} style={{ ...miniInput(0), flex: 1 }} />
                  <button className="btn btn-secondary btn-sm" disabled={busy} onClick={addTracking}>Add</button>
                </div>
              </Section>

              {/* activity */}
              <Section title={`Activity (${activity.length})`}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'flex-start' }}>
                  <select value={note.type} onChange={e => setNote(s => ({ ...s, type: e.target.value }))} style={miniInput(110)}>
                    <option value="note">Note</option><option value="reminder">Reminder</option><option value="approval">Approval</option><option value="hold">Hold</option>
                  </select>
                  <textarea placeholder="Add a note / reminder…" value={note.body} onChange={e => setNote(s => ({ ...s, body: e.target.value }))} rows={2} style={{ ...miniInput(0), flex: 1, fontFamily: 'inherit', resize: 'vertical' }} />
                  <button className="btn btn-primary btn-sm" disabled={busy || !note.body.trim()} onClick={addNote}>Post</button>
                </div>
                {activity.map(a => (
                  <div key={a.id} style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid #fafafa' }}>
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: '#f4f4f5', color: '#52525b', height: 'fit-content', textTransform: 'uppercase' }}>{a.type}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#0a0a0a' }}>{a.body}</div>
                      <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 1 }}>{a.actor || 'system'} · {fmtDateTime(a.occurred_at)}</div>
                    </div>
                  </div>
                ))}
                {activity.length === 0 && <Empty>No activity yet.</Empty>}
              </Section>
            </div>
          </>
        )}
      </aside>
    </>,
    document.body,
  );
}

// ── little presentational helpers ───────────────────────────────────────────
function Pill({ p }) { return <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: p.bg, color: p.fg }}>{p.label}</span>; }
function Field({ label, value, accent }) {
  return (
    <div style={{ background: '#fafafa', borderRadius: 8, padding: '7px 10px' }}>
      <div style={{ fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: accent || '#0a0a0a', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}
function FG({ label, children }) { return <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label" style={{ fontSize: 11 }}>{label}</label>{children}</div>; }
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0a0a0a', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ children }) { return <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 12, color: '#52525b' }}>{children}</div>; }
function Empty({ children }) { return <div style={{ fontSize: 11, color: '#a1a1aa' }}>{children}</div>; }
function ActBtn({ children, onClick, disabled, primary, danger }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        border: '1px solid ' + (primary ? '#0F8E6F' : danger ? '#fca5a5' : '#e4e4e7'),
        background: primary ? '#0F8E6F' : '#fff', color: primary ? '#fff' : danger ? '#dc2626' : '#0a0a0a',
        opacity: disabled ? 0.5 : 1,
      }}>{children}</button>
  );
}
const miniInput = (w) => ({ fontSize: 12, padding: '5px 8px', border: '1px solid #e4e4e7', borderRadius: 6, ...(w ? { width: w } : {}) });
const delBtn = { background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 14 };
